/* import __COLOCATED_TEMPLATE__ from './card-stats.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */
import Component from '@glimmer/component';
import type ContentStatistic from 'embercom/models/outbound/content-statistic';
import { statisticKeys } from 'embercom/models/data/outbound/constants';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  aiStats: ContentStatistic[];
  loadingAiStats: boolean;
}

export default class CardStats extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;

  /**
   * Check if dummy stats feature flag is enabled
   */
  get shouldUseDummyStats(): boolean {
    return this.appService.app.canUseFeature('conv-knowledge-con-dupe-dummy-stats');
  }

  /**
   * Cached dummy statistics to ensure consistency across multiple getter calls
   */
  private _dummyStats?: {
    receipts: number;
    finHelpfulAnswers: number;
    resolutionRate: number;
  };

  /**
   * Generate dummy statistics that maintain the resolution rate relationship
   */
  private generateDummyStats() {
    if (!this._dummyStats) {
      // Generate random receipts between 100-500
      let receipts = Math.floor(Math.random() * 401) + 100; // 100-500

      // Generate random resolution rate between 20-80%
      let resolutionRate = Math.floor(Math.random() * 61) + 20; // 20-80%

      // Calculate fin helpful answers to maintain the relationship
      let finHelpfulAnswers = Math.floor((receipts * resolutionRate) / 100);

      this._dummyStats = {
        receipts,
        finHelpfulAnswers,
        resolutionRate,
      };
    }
    return this._dummyStats;
  }

  get receiptsStatistic() {
    if (this.shouldUseDummyStats) {
      return { value: this.generateDummyStats().receipts };
    }
    return this.getStatObject('receipts');
  }

  get finHelpfulAnswersStatistic() {
    if (this.shouldUseDummyStats) {
      return { value: this.generateDummyStats().finHelpfulAnswers };
    }
    return this.getStatObject('finHelpfulAnswers');
  }

  get resolutionRate(): number {
    let receipts = this.receiptsStatistic?.value;
    let resolutions = this.finHelpfulAnswersStatistic?.value;

    if (!receipts || !resolutions || receipts === 0) {
      return 0;
    }

    let percentage = (resolutions / receipts) * 100;
    return Number(percentage.toFixed(2));
  }

  get receiptsStatisticValue() {
    return this.receiptsStatistic?.value || 0;
  }

  get finHelpfulAnswersStatisticValue() {
    return this.finHelpfulAnswersStatistic?.value || 0;
  }

  private getStatObject(statName: 'receipts' | 'finHelpfulAnswers') {
    let key = statisticKeys[statName];
    return this.args.aiStats.find((stat) => stat && stat.key === key) || null;
  }

  get tooltipText() {
    return this.intl.t('knowledge-hub.content-editor.side-by-side-review.card.tooltip-text');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::SideBySideReview::CardStats': typeof CardStats;
    'knowledge-hub/content-editor/side-by-side-review/card-stats': typeof CardStats;
  }
}
